import { supabase } from '@/lib/supabase';
import { getTenantId } from '@/repos/contentRepo';

export interface UserAvailability {
  id?: string;
  user_id: string;
  tenant_id?: string;
  day_of_week: number; // 0=Sunday, 1=Monday, etc.
  enabled: boolean;
  start_time?: string;
  end_time?: string;
  activity_type_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ActivityType {
  id?: string;
  user_id: string;
  tenant_id?: string;
  name: string;
  duration: number; // em minutos
  color: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface WeeklySchedule {
  [key: string]: {
    enabled: boolean;
    slots: Array<{
      id: string;
      startTime: string;
      endTime: string;
      activityType: string;
    }>;
  };
}

// Salvar disponibilidade semanal do usuário
export async function saveUserAvailability(availabilityData: UserAvailability[]): Promise<void> {
  try {
    const tenantId = await getTenantId();
    
    // Preparar dados com tenant_id
    const dataWithTenant = availabilityData.map(item => ({
      ...item,
      tenant_id: tenantId
    }));

    const { error } = await supabase
      .from('user_availability')
      .upsert(dataWithTenant, {
        onConflict: 'user_id,tenant_id,day_of_week'
      });

    if (error) throw error;
  } catch (error) {
    console.error('❌ Erro ao salvar disponibilidade do usuário:', error);
    throw error;
  }
}

// Buscar disponibilidade do usuário
export async function getUserAvailability(userId: string, tenantId?: string): Promise<UserAvailability[]> {
  try {
    const currentTenantId = tenantId || await getTenantId();
    
    const { data, error } = await supabase
      .from('user_availability')
      .select('*')
      .eq('user_id', userId)
      .eq('tenant_id', currentTenantId)
      .order('day_of_week', { ascending: true });

    if (error) throw error;
    return data as UserAvailability[] || [];
  } catch (error) {
    console.error('❌ Erro ao buscar disponibilidade do usuário:', error);
    throw error;
  }
}

// Salvar tipos de atividade
export async function saveActivityTypes(activityTypes: ActivityType[]): Promise<void> {
  try {
    const tenantId = await getTenantId();
    
    // Preparar dados com tenant_id
    const dataWithTenant = activityTypes.map(item => ({
      ...item,
      tenant_id: tenantId
    }));

    const { error } = await supabase
      .from('activity_types')
      .upsert(dataWithTenant, {
        onConflict: 'id'
      });

    if (error) throw error;
  } catch (error) {
    console.error('❌ Erro ao salvar tipos de atividade:', error);
    throw error;
  }
}

// Buscar tipos de atividade
export async function getActivityTypes(userId: string, tenantId?: string): Promise<ActivityType[]> {
  try {
    const currentTenantId = tenantId || await getTenantId();
    
    const { data, error } = await supabase
      .from('activity_types')
      .select('*')
      .eq('user_id', userId)
      .eq('tenant_id', currentTenantId)
      .order('name', { ascending: true });

    if (error) throw error;
    return data as ActivityType[] || [];
  } catch (error) {
    console.error('❌ Erro ao buscar tipos de atividade:', error);
    throw error;
  }
}

// Converter disponibilidade do banco para formato do componente
export function convertAvailabilityToWeeklySchedule(availability: UserAvailability[]): WeeklySchedule {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const schedule: WeeklySchedule = {};
  
  days.forEach((day, index) => {
    const dayAvailability = availability.find(a => a.day_of_week === index);
    
    if (dayAvailability && dayAvailability.enabled) {
      schedule[day] = {
        enabled: true,
        slots: dayAvailability.start_time && dayAvailability.end_time ? [{
          id: dayAvailability.id || `slot-${index}`,
          startTime: dayAvailability.start_time,
          endTime: dayAvailability.end_time,
          activityType: dayAvailability.activity_type_id || '1'
        }] : []
      };
    } else {
      schedule[day] = {
        enabled: false,
        slots: []
      };
    }
  });
  
  return schedule;
}

// Converter disponibilidade do componente para formato do banco
export function convertWeeklyScheduleToAvailability(
  weeklySchedule: WeeklySchedule, 
  userId: string
): UserAvailability[] {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const availability: UserAvailability[] = [];
  
  days.forEach((day, index) => {
    const daySchedule = weeklySchedule[day];
    
    if (daySchedule) {
      // Se o dia tem slots, criar uma entrada para cada slot
      if (daySchedule.enabled && daySchedule.slots.length > 0) {
        daySchedule.slots.forEach(slot => {
          availability.push({
            user_id: userId,
            day_of_week: index,
            enabled: true,
            start_time: slot.startTime,
            end_time: slot.endTime,
            activity_type_id: slot.activityType
          });
        });
      } else {
        // Dia habilitado mas sem slots específicos
        availability.push({
          user_id: userId,
          day_of_week: index,
          enabled: daySchedule.enabled,
          start_time: undefined,
          end_time: undefined,
          activity_type_id: undefined
        });
      }
    }
  });
  
  return availability;
}
